<script lang="ts">
	import throttle from 'lodash.throttle'
	import ChatBubble from './ChatBubble.svelte';
	import type { ChatObject } from '$lib/stores/chatStore';
	import { embeddedChatStore } from '$lib/stores/chatStore';
	import { appStore } from '$lib/stores/appStore';
	import { onMount } from 'svelte';
	import {
		updateText,
		isValidUrl
	} from '$lib/utils/utils';
	import PlusIcon from '../../icons/PlusIcon.svelte';
	import { openWebSocketConnection } from '$lib/utils/wsEndpoints';
	import ChatInput from './ChatInput.svelte';

	export let showChat: boolean;
	let chatBubblesElement: HTMLDivElement;
	let showStopGenerateButton: boolean = false;
	let inputValue: string = '';
	let containerRef: HTMLElement;
	let inputRef: HTMLTextAreaElement;
	let containerHeight: number = 50;
	let innerWidth: number;
	let resizeObserver: ResizeObserver;
	let scrollHeight: number;
	let questionBlockHeight: number = 0;
	let otherElementsHeight: number = 0;
	let isCustomizePage: boolean = false;
	let sidebarPosition: 'right' | 'left';
	let isRealData: boolean = false;
	let renderChat: ChatObject[];

	const testData = {
		"messages":[
			{
				"message":"Who will win the election?",
				"isUser":true,
				"isGenerating":false,
				"id":"730578dc-b868-4099-9493-511c4e6b698f",
				"time":"Fri May 31 2024 16:36:25 GMT+0300 (Eastern European Summer Time)",
				"hasError":false
			},
			{
				"message":"The upcoming elections in 2024 across various countries, including the US, are highly anticipated. In the US, the presidential showdown between Joe Biden and Donald Trump is crucial, with Biden emphasizing the importance of democracy in the face of autocratic challenges [2](https://www.pewresearch.org/politics/2024/04/24/feelings-about-the-2024-race-for-president/). The outcome of this election will have significant global implications, potentially impacting the international order and the balance between democracy and authoritarianism [4](https://www.theguardian.com/world/2024/feb/23/2024-global-elections-tracker-voting-dates-us-india-indonesia-belarus-haiti-pakistan-full-list). While Biden supporters believe the election outcome matters greatly, the share of voters who consider it crucial has slightly decreased compared to previous years [2](https://www.pewresearch.org/politics/2024/04/24/feelings-about-the-2024-race-for-president/). The results of the US election could influence the future of democracy worldwide, with implications for countries like Russia and China [1](https://www.theguardian.com/world/2023/dec/17/democracys-super-bowl-40-elections-that-will-shape-global-politics-in-2024). The election results are awaited with anticipation and could shape the course of global politics [1](https://www.theguardian.com/world/2023/dec/17/democracys-super-bowl-40-elections-that-will-shape-global-politics-in-2024).\n",
				"sourceText":"Who will win the election?",
				"isUser":false,
				"isGenerating":true,
				"id":"caf9829d-8a53-451b-9417-076a83d89263",
				"time":"Fri May 31 2024 16:36:25 GMT+0300 (Eastern European Summer Time)",
				"hasError":false,
				"chatId":"5f4a7a00-a963-4a4b-8601-21f4760b5bd1",
				"sources":[
					{
						"id":"1",
						"title":"Democracy’s Super Bowl: 40 elections that will shape global politics in 2024 | World news | The Guardian",
						"url":"https://www.theguardian.com/world/2023/dec/17/democracys-super-bowl-40-elections-that-will-shape-global-politics-in-2024"
					},
					{
						"id":"2",
						"title":"Feelings about the 2024 presidential election | Pew Research Center",
						"url":"https://www.pewresearch.org/politics/2024/04/24/feelings-about-the-2024-race-for-president/"
					},
					{
						"id":"3",
						"title":"Vote Smart - Facts For All",
						"url":"https://justfacts.votesmart.org/public-statement/1470135/cnn-presidential-town-hall-with-joe-biden/"
					},
					{
						"id":"4",
						"title":"Elections tracker 2024: every vote and why it matters | World news | The Guardian",
						"url":"https://www.theguardian.com/world/2024/feb/23/2024-global-elections-tracker-voting-dates-us-india-indonesia-belarus-haiti-pakistan-full-list"
					}
				]
			}
		]
	}
	$: {
		if (containerRef) {
			resizeObserver.observe(containerRef);
		} else {
			resizeObserver?.disconnect();
		}
	}
	$: showStopGenerateButton = $embeddedChatStore.messages[$embeddedChatStore.activeChatId]?.some(
		(item) => item.isGenerating && !item.message
	);

	$: otherElementsHeight = questionBlockHeight + containerHeight + 100 + (innerWidth < 800 ? 60 : 0);
	$: isCustomizePage = !!$appStore.chatWidgetParams.isCustomizePage && $appStore.chatWidgetParams.testMode === false;
	$: isRealData = !!$appStore.chatWidgetParams.isCustomizePage && $appStore.chatWidgetParams.testMode === true;
	$: renderChat = isCustomizePage ? testData.messages : $embeddedChatStore.messages[$embeddedChatStore.activeChatId];
	$: sidebarPosition = !!$appStore.chatWidgetParams.isCustomizePage ? 'right' : $appStore.chatWidgetParams.appearance.sidebarPosition;

	/**
	 * Add new message, system or user message
	 * @param data - information about message
	 * @param isUser - is user or server message
	 * @param isGenerating - is message generating, for user message it's false
	 * @param retryMessageId - message id for retry request
	 * */
	function addMessage(
		data: {
			message: string;
			sourceText?: string;
			defaultMessageNumber?: string;
		},
		isUser: boolean,
		isGenerating: boolean = false,
		retryMessageId?: string
	) {
		const chatObject: ChatObject = {
			...data,
			isUser: isUser,
			isGenerating,
			id: crypto.randomUUID(),
			time: new Date().toString(),
			hasError: false
		};
		if (retryMessageId) {
			const index = $embeddedChatStore.messages[$embeddedChatStore.activeChatId].findIndex(
				(item) => item.id === retryMessageId
			);
			$embeddedChatStore.messages[$embeddedChatStore.activeChatId].splice(index, 1, chatObject);
		} else {
			$embeddedChatStore.messages[$embeddedChatStore.activeChatId] = [
				...$embeddedChatStore.messages[$embeddedChatStore.activeChatId],
				chatObject
			];
		}
		embeddedChatStore.update((value) => value);
		scrollDown();
		return chatObject;
	}

	function handleSendMessage() {
		if ((!inputValue) || showStopGenerateButton || isCustomizePage) return;

		addMessage({ message: inputValue }, true, false);
		createMessageNew(inputValue, '');
		inputValue = '';
	}

	const scrollDownMessage = throttle((firstRun?: boolean) => {
		if (firstRun) {
			scrollHeight = chatBubblesElement.scrollHeight;
		}
		if (scrollHeight > chatBubblesElement.scrollHeight - (innerWidth < 800 ? 150 : 300)) {
			scrollDown();
		}
	}, 1000);

	const scrollDown = () => {
		setTimeout(() => {
			if (chatBubblesElement) {
				chatBubblesElement.scrollTop = chatBubblesElement.scrollHeight;
			}
		}, 100);
	}

	/**
	 * Processing messages from a websocket. The message can be just a string (this is a fragment of the response),
	 * a string with the state of the agent on the server, and an object that includes information about the chat and sources.
	 *
	 * @param event - websocket event
	 * @param socket - websocket
	 * @param newMessage - message object
	 * @param isMessageFromNewChat
	 * @param stopLoading - stop loading function
	 * @param updateStore - update embeddedChatStore function
	 */
	const onDataReceived = (event: MessageEvent, socket: WebSocket, newMessage: ChatObject, isMessageFromNewChat: boolean, stopLoading: (error: string, message: ChatObject, isFinal?: boolean) => void, updateStore: () => void) => {
		if (typeof event.data === 'string' && event.data.trim().startsWith('{')) {
			try {
				const data: {
					avi_answer: string;
					avi_sources: { id: string; title: string; url: string };
					avi_images: string;
					avi_events: { value: string };
				} = JSON.parse(event.data);
				if (data.avi_sources && data.avi_sources.id) {
					if (!newMessage.sources) {
						newMessage.sources = [];
					}
					if (isValidUrl(data.avi_sources.url)) {
						newMessage.sources.push(data.avi_sources);
						updateStore();
					}
				}
				if (data.avi_images) {
					if (!newMessage.images) {
						newMessage.images = [];
					}
					newMessage.images.push(data.avi_images);
				}
				if (data.avi_answer) {
					if (!newMessage.message && !!(newMessage.message + data.avi_answer).trim()) {
						stopLoading('', newMessage, false);
						setTimeout(() => {
							updateStore();
							// scrollDownMessage(true);
						}, 10);
					} else {
						setTimeout(() => {
							// scrollDownMessage();
						}, 10);
					}

					newMessage.message += data.avi_answer;
					newMessage.message = updateText(newMessage.message, newMessage.sources || []).replace(/ {3}/g, "");
					$appStore.animatedMessagesData[newMessage.id].message = newMessage.message;
				}
				if (data.avi_events && data.avi_events.value) {
					switch (data.avi_events.value) {
						case "Done Task: Sources":
							$appStore.animatedMessagesData[newMessage.id].sourcesTaskIsDone = true;
							updateStore();
							break;
						case "Done Task: Answer":
							$appStore.animatedMessagesData[newMessage.id].answerTaskIsDone = true;
							$appStore.animatedMessageIds = $appStore.animatedMessageIds.filter(item => item !== newMessage.id);
							updateStore();
							break;
						case "Done Task: Images":
							$appStore.animatedMessagesData[newMessage.id].imagesTaskIsDone = true;
							updateStore();
							break;
						default:
							console.log('Received Intermediate Steps =======> ', data.avi_events.value);
							break;
					}
				}
			} catch (e) {
				console.log(e);
			}
		}
	};

	/**
	 * This function is called when the socket is closed. The socket can be closed on error or when the response generation process completes
	 * @param newMessage - message object
	 * @param data - chat data
	 * @param stopLoading - stop loading function
	 * @param updateStore - update store function
	 */
	const onSocketClose = (newMessage: ChatObject, data: { id: string, title: string, createdAt: string, loadingTitle: string }, stopLoading: (error: string, message: ChatObject) => void, updateStore: () => void) => {
		if (newMessage.message.length === 0) {
			newMessage.defaultMessageNumber = '4';
			newMessage.hasError = true;
			newMessage.isGenerating = false;
			stopLoading('', newMessage);
			updateStore();
			scrollDown();
		}
	};

	/**
	 * Preparing data and executing the query
	 *
	 * @param string -
	 * @param retryMessageId
	 */
	async function createMessageNew(string: string, retryMessageId?: string) {
		const isNewChat = $embeddedChatStore.activeChatId.includes('new-chat-');
		const params: {
			question: string;
			language: string;
			chatId: string;
			widget: string;
			host: string;
			responseLength: number;
			widgetKey: string;
			instructions: string;
		} = {
			question: string,
			language: 'English',
			chatId: isNewChat ? '' : $embeddedChatStore.activeChatId,
			widget: "chat",
			host: $appStore.chatWidgetParams.response.host, // location.hostname,
			responseLength: $appStore.chatWidgetParams.response.responseLength,
			widgetKey: $appStore.chatWidgetParams.widgetKey,
			instructions: $appStore.chatWidgetParams.response.instructions
		};
		const newMessage: ChatObject = addMessage(
			{ message: '', sourceText: string },
			false,
			true,
			retryMessageId
		);
		newMessage.chatId = $embeddedChatStore.activeChatId;
		const updateStore = () => {
			embeddedChatStore.update((value) => value);
		};
		const stopLoading = (error: string = '', message: ChatObject, isFinal: boolean = true) => {
			message.isGenerating = !isFinal;
			updateStore();
		};
		const isMessageFromNewChat = newMessage.chatId.includes('new-chat-');
		let data = {
			id: newMessage.chatId,
			title: newMessage.sourceText || 'new chat',
			createdAt: new Date().toString(),
			loadingTitle: params.question
		};
		try {
			//Function for opening Websocket connection and sending message
			$appStore.animatedMessagesData[newMessage.id] = { message: ''};
			$appStore.animatedMessageIds.push(newMessage.id);
			openWebSocketConnection(
				isRealData,
				params,
				onDataReceived,
				onSocketClose,
				newMessage,
				isMessageFromNewChat,
				stopLoading,
				updateStore,
				data
			);
		} catch (e) {
			$appStore.animatedMessageIds = $appStore.animatedMessageIds.filter(item => item !== newMessage.id);
			newMessage.defaultMessageNumber = '4';
			newMessage.hasError = true;
			newMessage.isGenerating = false;
			stopLoading('', newMessage);
			updateStore();
			// scrollDown();
		}
	}

	onMount(() => {
		if (!$embeddedChatStore.messages[$embeddedChatStore.activeChatId]?.length) {
			if (!$embeddedChatStore.messages[$embeddedChatStore.activeChatId]) {
				$embeddedChatStore.activeChatId = crypto.randomUUID();
				$embeddedChatStore.messages[$embeddedChatStore.activeChatId] = [];
				$embeddedChatStore.isLoaded = true;
			}
		}
		resizeObserver = new ResizeObserver((entries) => {
			// We're only watching one element
			const entry = entries.at(0);

			if (entry?.contentBoxSize[0]) {
				containerHeight = entry?.contentBoxSize[0].blockSize;
			}
		});

		return () => {
			resizeObserver.disconnect();
		};
	});
</script>

<svelte:window bind:innerWidth />

<div class="main-wrapper">
	<div class="chat">
		<div class="content">
			<div
				class="chat-bubbles-wrapper"
				bind:this={chatBubblesElement}
			>
				<div class="chat-bubbles">
					{#if renderChat}
						{#each renderChat as chatObject}
							{#key chatObject.id}
								<ChatBubble
									message={chatObject}
									retryFunction={createMessageNew}
									bind:questionBlockHeight
									{otherElementsHeight}
								/>
							{/key}
						{/each}
					{/if}
				</div>
			</div>
			<div
				role="presentation"
				class="input-wrapper"
				bind:this={containerRef}
				class:newChat={!$embeddedChatStore.messages[$embeddedChatStore.activeChatId]?.length}
			>
				<ChatInput
					bind:inputRef
					bind:inputValue
					{handleSendMessage}
					{showStopGenerateButton}
				/>
			</div>
			<div class="chat-footer" class:reverse={sidebarPosition === 'left'}>
				<div class="logo-wrapper">
					<span>Powered by</span>
					<div class="image-wrapper">
						<img alt='logo' src='{import.meta.env.VITE_HOST}/chat-logo.png' />
						<span>Deep</span>
					</div>
				</div>
				<button on:click={() => showChat = false} class="close-chat-button">
					<PlusIcon/>
				</button>
			</div>
		</div>
	</div>
</div>

<style lang="scss">
	.main-wrapper {
		height: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		color: var(--text-color);
		position: relative;
	}

	.chat {
		display: flex;
		width: 100%;
		justify-content: center;
		transition: 200ms;
		position: relative;
	}

	.content {
		width: 100%;
		height: 100vh;
		height: 100svh;
		display: grid;
		grid-template-rows: 1fr auto auto;
	}

	.chat-bubbles-wrapper {
		width: 100%;
		overflow: scroll;
		scroll-behavior: smooth;
		display: flex;
		align-items: center;
		flex-direction: column;
		padding-bottom: 40px;

		/* Hide scrollbar for Chrome, Safari and Opera */
		&::-webkit-scrollbar {
			display: none;
		}

		-ms-overflow-style: none; /* IE and Edge */
		scrollbar-width: none; /* Firefox */

		mask-image: linear-gradient(180deg, transparent 20px, #000 50px, #000 calc(100% - 40px), transparent calc(100% - 10px));
		-webkit-mask-image: linear-gradient(180deg, transparent 20px, #000 50px, #000 calc(100% - 40px), transparent calc(100% - 10px));

		@media screen and (max-width: 1200px) {
			width: 100%;
		}
		.chat-bubbles {
			max-width: 800px;
			display: flex;
			flex-direction: column;
			padding-left: 16px;
			padding-right: 16px;
			width: 100%;
			box-sizing: border-box;
		}
	}

	.input-wrapper {
		width: 100%;
		display: flex;
		justify-content: center;
		transition: all 0.2s;

		&.newChat {
			bottom: calc(50% - 100px);
		}
	}
	.chat-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 16px 16px 20px 19px;
		&.reverse {
			flex-direction: row-reverse;
		}

		.logo-wrapper {
			span {
				color: var(--button-color);
				font-size: 14px;
				padding-left: 4px;
			}
			.image-wrapper {
				display: flex;
				align-items: center;
				justify-content: center;
				img {
					width: 32px;
					height: 32px;
				}
				span {
					font-size: 22px;
					font-weight: 600;
					line-height: 26.63px;
				}
			}
		}

		.close-chat-button {
			width: 46px;
			height: 46px;
			border: 1px var(--button-border-color) solid;
			border-radius: 50%;
			background: var(--button-bg-alternative);
			display: flex;
			align-items: center;
			justify-content: center;
			cursor: pointer;
			color: var(--button-color);
			transform: rotate(45deg);
			padding: 8px;
		}
	}
</style>
